import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { scooterApi, formatPrice, getFileUrl } from "@/lib/api";
import { 
  Facebook, 
  Instagram, 
  Copy, 
  Check, 
  ExternalLink, 
  Share2,
  Image,
  FileText,
  AlertCircle,
  CheckCircle,
  Clock,
  MessageCircle,
  Phone
} from "lucide-react";

// Contact info
const CONTACT_INFO = {
  phone: "0792250058",
  whatsapp: "40792250058",
  email: "office@scutersh.com"
};

// Generate Facebook share URL with OG tags
const generateFacebookShareUrl = (scooter) => {
  // Use the special /api/share/ endpoint that has OG tags
  const shareUrl = `${window.location.origin}/api/share/scooter/${scooter.scooter_id}`;
  return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
};

// Get the share link for copying
const getShareLink = (scooter) => {
  return `${window.location.origin}/api/share/scooter/${scooter.scooter_id}`;
};

// Generate social media content
const generateSocialContent = (scooter, includeSheet = false) => {
  const photos = scooter.photos || [];
  const qrUrl = `${window.location.origin}/scooter/${scooter.scooter_id}`;
  const showroomUrl = `${window.location.origin}/showroom`;
  
  const roText = `🛵 DE VÂNZARE: ${scooter.brand} ${scooter.model} ${scooter.year}

💰 Preț: ${formatPrice(scooter.selling_price)}
${scooter.selling_price >= 3000 ? '📦 Preț negociabil pentru achiziții în lot/en-gros' : ''}

⭐ DE CE SĂ CUMPERI DE LA NOI?
✅ Dosar digital complet - vezi tot istoricul și starea scuterului
✅ Ne ocupăm de toate reparațiile și reviziile
✅ Calitate verificată și garantată

📋 Specificații:
• An: ${scooter.year} | Motor: ${scooter.engine_cc}cc
• Km: ${scooter.mileage.toLocaleString()} km | Culoare: ${scooter.color}
• ${scooter.mechanical_check?.aprobat_utilizare !== false ? '✅ Verificat și aprobat' : '⚠️ Necesită verificare'}

📍 București | 🚚 Transport în toată România

📱 Vezi DOSARUL COMPLET (istoric, verificări, poze):
🔗 ${qrUrl}

📞 ${CONTACT_INFO.phone} | 📧 ${CONTACT_INFO.email}

👀 Toate scuterele: ${showroomUrl}

#Scutersh #Scooter #ScooterDeVanzare #${scooter.brand.replace(/\s/g, '')} #Bucuresti #Romania #ScuterVerificat`;

  const enText = `🛵 FOR SALE: ${scooter.brand} ${scooter.model} ${scooter.year}

💰 Price: ${formatPrice(scooter.selling_price)}
${scooter.selling_price >= 3000 ? '📦 Price negotiable for bulk purchases' : ''}

⭐ WHY BUY FROM US?
✅ Complete digital file - see full history and condition
✅ We handle all repairs and servicing
✅ Verified quality guaranteed

📋 Specs:
• Year: ${scooter.year} | Engine: ${scooter.engine_cc}cc
• Mileage: ${scooter.mileage.toLocaleString()} km | Color: ${scooter.color}
• ${scooter.mechanical_check?.aprobat_utilizare !== false ? '✅ Verified and approved' : '⚠️ Needs inspection'}

📍 Bucharest | 🚚 Delivery across Romania

📱 See COMPLETE FILE (history, checks, photos):
🔗 ${qrUrl}

📞 ${CONTACT_INFO.phone} | 📧 ${CONTACT_INFO.email}

👀 All scooters: ${showroomUrl}

#Scutersh #Scooter #ScooterForSale #${scooter.brand.replace(/\s/g, '')} #Bucharest #Romania #VerifiedScooter`;

  return { roText, enText, photos, qrUrl, showroomUrl };
};

// Generate OLX content
const generateOLXContent = (scooter) => {
  const title = `${scooter.brand} ${scooter.model} ${scooter.year} - ${scooter.engine_cc}cc - ${scooter.mileage.toLocaleString()}km`;
  const showroomUrl = `${window.location.origin}/showroom`;
  const scooterUrl = `${window.location.origin}/scooter/${scooter.scooter_id}`;
  
  const description = `${scooter.brand} ${scooter.model} - An ${scooter.year}

===============================
DE CE SA CUMPERI DE LA NOI?
===============================
[+] TRANSPARENTA TOTALA - Fiecare scuter are un DOSAR DIGITAL COMPLET cu:
    - Istoricul complet al reparatiilor si reviziilor
    - Verificare tehnica detaliata (40+ puncte de control)
    - Fotografii din toate unghiurile
    - Fisa tehnica completa accesibila prin cod QR

[+] NE OCUPAM DE TOATE REPARATIILE SI REVIZIILE
    - Scuterele sunt verificate si pregatite pentru utilizare
    - Schimburi de ulei, filtre, curele efectuate
    - Reparatii mecanice si electrice realizate inainte de vanzare
    - Garantie pe lucrarile efectuate

[+] CALITATE GARANTATA
    - Selectam doar scutere in stare buna
    - Verificare completa inainte de punerea in vanzare
    - Consultanta si suport post-vanzare

Vezi DOSARUL COMPLET al acestui scuter:
${scooterUrl}

===============================
SPECIFICATII TEHNICE
===============================
Marca: ${scooter.brand}
Model: ${scooter.model}
An fabricatie: ${scooter.year}
Capacitate cilindrica: ${scooter.engine_cc}cc
Kilometraj: ${scooter.mileage.toLocaleString()} km
Culoare: ${scooter.color}
VIN/Sasiu: ${scooter.vin}

===============================
ACTE SI DOCUMENTE
===============================
${scooter.mechanical_check?.acte_disponibile !== false ? '[+] Acte disponibile' : '[-] Fara acte (doar factura)'}
${scooter.mechanical_check?.tip_acte === 'carte_rar' ? '- Carte RAR Romania' : 
  scooter.mechanical_check?.tip_acte === 'carte_strainatate' ? '- Carte de strainatate (Carte RO la cerere)' : 
  '- Doar factura'}

===============================
STARE TEHNICA
===============================
${scooter.mechanical_check?.aprobat_utilizare !== false ? '[+] VERIFICAT SI APROBAT PENTRU UTILIZARE' : '[!] Necesita verificari suplimentare'}
- Scor tehnic general: ${scooter.mechanical_check?.scor_general || 3}/5
- Verificare completa: Motor, Transmisie, Frane, Electrica, Caroserie
${scooter.mechanical_check?.recomandari_mecanic ? `- Observatii: ${scooter.mechanical_check.recomandari_mecanic}` : ''}

===============================
PRET SI CONDITII
===============================
Pret: ${formatPrice(scooter.selling_price)}
${scooter.selling_price >= 3000 ? 'Pret negociabil pentru achizitii in lot/en-gros (2+ scutere)' : ''}

===============================
LOCATIE SI LIVRARE
===============================
- Bucuresti, Romania
- Posibilitate transport in toata Romania (contra cost)
- Vizionare doar cu programare

===============================
SCUTERSH
===============================
Scutere & Piese Premium | Calitate garantata | Transparenta totala

Telefon/WhatsApp: ${CONTACT_INFO.phone}

Vezi toate scuterele disponibile: ${showroomUrl}
Vezi dosarul complet al acestui scuter: ${scooterUrl}

===============================
ENGLISH VERSION
===============================
${scooter.brand} ${scooter.model} ${scooter.year}
Engine: ${scooter.engine_cc}cc | Mileage: ${scooter.mileage.toLocaleString()}km
Price: ${formatPrice(scooter.selling_price)}

WHY BUY FROM US?
[+] Full transparency - Each scooter has a COMPLETE DIGITAL FILE with repair history, technical verification, and photos
[+] We handle all repairs and servicing before sale
[+] Quality guaranteed - Only selected scooters in good condition

See the complete file: ${scooterUrl}

Location: Bucharest, Romania
Delivery available throughout Romania (extra fee)
${scooter.mechanical_check?.aprobat_utilizare !== false ? '[+] Verified and approved for use' : ''}

Phone/WhatsApp: ${CONTACT_INFO.phone}

See all available scooters: ${showroomUrl}`;

  return { title, description, price: scooter.selling_price };
};

// Publishing status badge
const PublishStatusBadge = ({ status }) => {
  const statusConfig = {
    draft: { label: "Draft", color: "bg-zinc-500", icon: Clock },
    published_social: { label: "Publicat Social", color: "bg-blue-500", icon: Share2 },
    published_marketplace: { label: "Publicat OLX", color: "bg-orange-500", icon: ExternalLink },
    published_all: { label: "Publicat Tot", color: "bg-green-500", icon: CheckCircle },
  };
  
  const config = statusConfig[status] || statusConfig.draft;
  const Icon = config.icon;
  
  return (
    <Badge className={`${config.color} text-white`}>
      <Icon className="w-3 h-3 mr-1" />
      {config.label}
    </Badge>
  );
};

// Main Publishing Center Component
export const PublishingCenter = ({ scooter, onStatusUpdate }) => {
  const [copied, setCopied] = useState(null);
  const [publishStatus, setPublishStatus] = useState({
    social_media: scooter.publish_status?.social_media || false,
    marketplace: scooter.publish_status?.marketplace || false,
  });

  // Validation checks
  const hasPhotos = scooter.photos && scooter.photos.length > 0;
  const hasPrice = scooter.selling_price > 0;
  const hasDocuments = scooter.mechanical_check?.acte_disponibile !== false || 
                       scooter.mechanical_check?.tip_acte === 'fara_acte';
  const canPublish = hasPhotos && hasPrice;

  const socialContent = generateSocialContent(scooter);
  const olxContent = generateOLXContent(scooter);

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(type);
      toast.success("Copiat în clipboard!");
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      toast.error("Eroare la copiere");
    }
  };

  const handleStatusChange = async (platform, checked) => {
    const newStatus = { ...publishStatus, [platform]: checked };
    setPublishStatus(newStatus);
    
    try {
      await scooterApi.update(scooter.scooter_id, {
        publish_status: newStatus
      });
      toast.success(`Status actualizat: ${platform}`);
      if (onStatusUpdate) onStatusUpdate(newStatus);
    } catch (err) {
      toast.error("Eroare la actualizare status");
    }
  };

  const openOLX = () => {
    copyToClipboard(olxContent.description, 'olx');
    // Open OLX posting page
    window.open('https://www.olx.ro/d/oferta/publicate/', '_blank');
    toast.success("Descrierea a fost copiată! Lipește-o în formularul OLX.");
  };

  // Generate WhatsApp link with pre-filled message
  const generateWhatsAppLink = () => {
    const scooterUrl = `${window.location.origin}/scooter/${scooter.scooter_id}`;
    const message = `Bună! Sunt interesat de scuterul ${scooter.brand} ${scooter.model} ${scooter.year} (${formatPrice(scooter.selling_price)}).

🔗 Link: ${scooterUrl}

Aș dori mai multe informații. Mulțumesc!`;
    
    const encodedMessage = encodeURIComponent(message);
    return `https://wa.me/${CONTACT_INFO.whatsapp}?text=${encodedMessage}`;
  };

  const openWhatsApp = () => {
    window.open(generateWhatsAppLink(), '_blank');
    toast.success("Se deschide WhatsApp...");
  };

  if (!canPublish) {
    return (
      <Card className="border-destructive/50 bg-destructive/5">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg text-destructive">
            <AlertCircle className="w-5 h-5" />
            Nu se poate publica
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm">
            {!hasPhotos && (
              <div className="flex items-center gap-2 text-destructive">
                <Image className="w-4 h-4" />
                <span>Adaugă cel puțin o poză</span>
              </div>
            )}
            {!hasPrice && (
              <div className="flex items-center gap-2 text-destructive">
                <FileText className="w-4 h-4" />
                <span>Setează prețul de vânzare</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-primary/30">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Share2 className="w-5 h-5 text-primary" />
            Centru Publicare
          </CardTitle>
          <div className="flex gap-2">
            {publishStatus.social_media && <PublishStatusBadge status="published_social" />}
            {publishStatus.marketplace && <PublishStatusBadge status="published_marketplace" />}
            {!publishStatus.social_media && !publishStatus.marketplace && <PublishStatusBadge status="draft" />}
          </div>
        </div>
        <p className="text-sm text-muted-foreground">
          „Adaugi scuterul o dată. Apare peste tot."
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Status Tracking */}
        <div className="bg-secondary/30 rounded-lg p-4">
          <h4 className="font-bold text-sm mb-3">📊 Status Publicare</h4>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center gap-2">
              <Checkbox
                id="status_social"
                checked={publishStatus.social_media}
                onCheckedChange={(checked) => handleStatusChange('social_media', checked)}
              />
              <Label htmlFor="status_social" className="text-sm cursor-pointer">
                Publicat pe Social Media
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="status_marketplace"
                checked={publishStatus.marketplace}
                onCheckedChange={(checked) => handleStatusChange('marketplace', checked)}
              />
              <Label htmlFor="status_marketplace" className="text-sm cursor-pointer">
                Publicat pe OLX/Marketplace
              </Label>
            </div>
          </div>
        </div>

        <Separator />

        {/* Facebook Direct Share Section */}
        <div className="bg-blue-500/10 rounded-lg p-4 border border-blue-500/30">
          <h4 className="font-bold text-sm mb-3 flex items-center gap-2">
            <Facebook className="w-5 h-5 text-blue-600" />
            Partage Facebook Optimisé
          </h4>
          <p className="text-xs text-muted-foreground mb-4">
            Ce lien affiche une preview optimisée sur Facebook avec photo, titre et prix.
          </p>
          
          {/* Share Link */}
          <div className="flex gap-2 mb-3">
            <input
              type="text"
              readOnly
              value={getShareLink(scooter)}
              className="flex-1 px-3 py-2 bg-background border border-border rounded-md text-xs font-mono"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => copyToClipboard(getShareLink(scooter), 'fb_link')}
            >
              {copied === 'fb_link' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </Button>
          </div>
          
          {/* Action Buttons */}
          <div className="flex flex-wrap gap-2">
            <Button
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => window.open(generateFacebookShareUrl(scooter), '_blank', 'width=600,height=400')}
              data-testid="btn-share-facebook"
            >
              <Facebook className="w-4 h-4 mr-2" />
              Partager sur Facebook
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                copyToClipboard(getShareLink(scooter), 'fb_link');
                toast.success("Lien copié! Collez-le dans Facebook Messenger ou un post.");
              }}
            >
              <Copy className="w-4 h-4 mr-2" />
              Copier le lien
            </Button>
          </div>
          
          <p className="text-xs text-muted-foreground mt-3">
            💡 Utilisez ce lien pour: Posts Facebook, Messenger, Facebook Marketplace, Groupes
          </p>
        </div>

        <Separator />

        {/* Social Media Section */}
        <div>
          <h4 className="font-bold text-sm mb-3 flex items-center gap-2">
            <Facebook className="w-4 h-4 text-blue-600" />
            <Instagram className="w-4 h-4 text-pink-600" />
            Facebook / Instagram
          </h4>
          
          {/* Romanian Version */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-muted-foreground">🇷🇴 Versiune Română</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => copyToClipboard(socialContent.roText, 'social_ro')}
              >
                {copied === 'social_ro' ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                Copiază
              </Button>
            </div>
            <Textarea
              value={socialContent.roText}
              readOnly
              className="text-xs h-32 font-mono"
            />
          </div>

          {/* English Version */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-muted-foreground">🇬🇧 English Version</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => copyToClipboard(socialContent.enText, 'social_en')}
              >
                {copied === 'social_en' ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                Copy
              </Button>
            </div>
            <Textarea
              value={socialContent.enText}
              readOnly
              className="text-xs h-32 font-mono"
            />
          </div>

          {/* Photos info */}
          <div className="mt-3 p-3 bg-blue-500/10 rounded-lg">
            <p className="text-xs text-blue-600">
              📸 {scooter.photos?.length || 0} poze disponibile pentru postare
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Nu uita să adaugi și poza cu fișa tehnică (Printează Fișa → Salvează ca imagine)
            </p>
          </div>
        </div>

        <Separator />

        {/* OLX Section */}
        <div>
          <h4 className="font-bold text-sm mb-3 flex items-center gap-2">
            <ExternalLink className="w-4 h-4 text-orange-500" />
            OLX / Marketplace
          </h4>
          
          {/* Title */}
          <div className="mb-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground">Titlu OLX</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => copyToClipboard(olxContent.title, 'olx_title')}
              >
                {copied === 'olx_title' ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
              </Button>
            </div>
            <div className="bg-secondary p-2 rounded text-sm font-mono">
              {olxContent.title}
            </div>
          </div>

          {/* Description */}
          <div className="mb-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground">Descriere completă</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => copyToClipboard(olxContent.description, 'olx_desc')}
              >
                {copied === 'olx_desc' ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                Copiază
              </Button>
            </div>
            <Textarea
              value={olxContent.description}
              readOnly
              className="text-xs h-48 font-mono"
            />
          </div>

          {/* Price */}
          <div className="mb-4 p-3 bg-orange-500/10 rounded-lg">
            <p className="text-sm font-bold text-orange-600">
              💰 Preț: {formatPrice(scooter.selling_price)}
            </p>
            <p className="text-xs text-muted-foreground">
              Categorie OLX: Auto, moto și ambarcațiuni → Scutere - Loss
            </p>
          </div>

          {/* OLX Button */}
          <Button
            className="w-full bg-orange-500 hover:bg-orange-600 text-white"
            onClick={openOLX}
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            Publică pe OLX
          </Button>
          <p className="text-xs text-muted-foreground mt-2 text-center">
            Se copiază descrierea și se deschide OLX în tab nou
          </p>
        </div>

        <Separator />

        {/* WhatsApp Direct Contact Section */}
        <div>
          <h4 className="font-bold text-sm mb-3 flex items-center gap-2">
            <MessageCircle className="w-4 h-4 text-green-500" />
            Contact Direct WhatsApp
          </h4>
          
          <div className="p-4 bg-green-500/10 rounded-lg mb-3">
            <p className="text-sm text-green-600 mb-2">
              📱 Clientul poate te contacta direct pe WhatsApp cu un mesaj pre-completat care include linkul scuterului.
            </p>
            <p className="text-xs text-muted-foreground">
              Număr: {CONTACT_INFO.phone}
            </p>
          </div>

          {/* WhatsApp Preview */}
          <div className="mb-3 p-3 bg-secondary rounded-lg">
            <p className="text-xs text-muted-foreground mb-1">Mesaj pre-completat:</p>
            <p className="text-xs font-mono">
              Bună! Sunt interesat de scuterul {scooter.brand} {scooter.model} {scooter.year} ({formatPrice(scooter.selling_price)}).
            </p>
            <p className="text-xs font-mono text-primary mt-1">
              🔗 {window.location.origin}/scooter/{scooter.scooter_id}
            </p>
          </div>

          {/* WhatsApp Button */}
          <Button
            className="w-full bg-green-500 hover:bg-green-600 text-white"
            onClick={openWhatsApp}
            data-testid="btn-whatsapp-contact"
          >
            <MessageCircle className="w-4 h-4 mr-2" />
            Deschide WhatsApp
          </Button>
          <p className="text-xs text-muted-foreground mt-2 text-center">
            Se deschide WhatsApp cu mesaj pre-completat pentru client
          </p>

          {/* Copy WhatsApp Link */}
          <div className="mt-3 flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="flex-1"
              onClick={() => {
                copyToClipboard(generateWhatsAppLink(), 'whatsapp_link');
              }}
            >
              {copied === 'whatsapp_link' ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
              Copiază link WhatsApp
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="flex-1"
              onClick={() => {
                const scooterUrl = `${window.location.origin}/scooter/${scooter.scooter_id}`;
                copyToClipboard(scooterUrl, 'scooter_link');
              }}
            >
              {copied === 'scooter_link' ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
              Copiază link scooter
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PublishingCenter;
